import { Combobox } from "@headlessui/react";
import { createPortal } from "react-dom";
import { useEffect, useRef, useState, type SyntheticEvent } from "react";
import { ChevronDown, Info, X } from "lucide-react";

import { AutoCompleteOption, AutoCompleteProps } from "./types";
import { autoCompleteStyles } from "./styles";
import { AutoCompleteContent } from "./AutoCompleteContent";
import { useAutoComplete } from "./hooks/useAutoComplete";
import { useAutoCompleteMulti } from "./hooks/useAutoCompleteMulti";

import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";

export const AutoComplete = ({
  size = "medium",
  label,
  tooltip,
  isDisabled = false,
  placeholder = "Select an option...",
  options,
  value,
  onSelect,
  multi = false,
  className,
  selectedValues = [],
  onSelectionChange,
  isDisplayed = true,
  addNewOption = true,
  readOnly = false,
  getAddOptionText,
  renderOption,
  onInputChange,
  isLoading = false,
  renderSelectedItem,
  maxVisibleTags,
  popoverMinWidth,
}: AutoCompleteProps) => {
  const customItemsRef = useRef<Map<string, AutoCompleteOption>>(new Map());
  const inputRef = useRef<HTMLInputElement>(null);
  const [isSelectionOverflowing, setIsSelectionOverflowing] = useState(false);

  const core = useAutoComplete({
    options,
    value,
    onSelect,
    multi,
    addNewOption,
    onInputChange,
    popoverMinWidth,
  });

  const multiState = useAutoCompleteMulti({
    options,
    selectedValues,
    onSelectionChange: onSelectionChange ?? (() => undefined),
    readOnly,
    setQuery: core.setQuery,
    customItemsRef,
    maxVisibleTags,
  });

  useEffect(() => {
    if (multi) {
      return;
    }
    const el = inputRef.current;
    if (!el) {
      return;
    }
    const check = () => setIsSelectionOverflowing(el.scrollWidth > el.clientWidth);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [multi, core.selectedItem, value]);

  const selectedLabel = core.selectedItem?.label || value || "";

  if (multi && onSelectionChange) {
    const {
      selectedItems,
      handleMultiComboboxChange,
      handleMultiRemove,
      visibleTags,
      hiddenTags,
      hasOverflow,
    } = multiState;

    return (
      <div
        className={cn(autoCompleteStyles.container({ size }), className, {
          hidden: !isDisplayed,
        })}
      >
        {label && (
          <div className="flex items-center gap-2">
            <label className={autoCompleteStyles.label({ size })}>{label}</label>
            {tooltip && (
              <BasicTooltip content={tooltip}>
                <Info className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700" />
              </BasicTooltip>
            )}
          </div>
        )}
        <Combobox
          disabled={isDisabled || readOnly}
          value={selectedItems}
          onChange={handleMultiComboboxChange}
          multiple
        >
          <div ref={core.refs.setReference} className="w-full">
            <Combobox.Button
              className={cn(
                "flex h-[38px] w-full items-center justify-between rounded-md border border-input px-2 text-sm focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500",
                isDisabled && "pointer-events-none cursor-not-allowed opacity-50",
              )}
            >
              <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-1 overflow-hidden">
                {visibleTags.map(tagValue => {
                  const optLabel = options.find(o => o.id === tagValue)?.label ?? tagValue;
                  const onRemove = !readOnly ? () => handleMultiRemove(tagValue) : undefined;
                  const stopComboboxActivation = (event: SyntheticEvent) => {
                    event.preventDefault();
                    event.stopPropagation();
                  };
                  if (renderSelectedItem) {
                    return (
                      <span
                        key={tagValue}
                        className="shrink-0"
                        onMouseDown={stopComboboxActivation}
                      >
                        {renderSelectedItem(tagValue, optLabel, onRemove)}
                      </span>
                    );
                  }
                  const variant = BadgeVariant.GRAY;
                  return (
                    <span key={tagValue} className="shrink-0" onMouseDown={stopComboboxActivation}>
                      <Badge
                        variant={variant}
                        size={BadgeSize.SM}
                        text={optLabel}
                        onRemove={onRemove}
                        className="shrink-0"
                      />
                    </span>
                  );
                })}
                {hasOverflow && (
                  <BasicTooltip
                    content={hiddenTags
                      .map(v => options.find(o => o.id === v)?.label ?? v)
                      .join(", ")}
                  >
                    <Badge
                      variant={BadgeVariant.GRAY}
                      size={BadgeSize.SM}
                      text={`+${hiddenTags.length}`}
                      className="shrink-0"
                    />
                  </BasicTooltip>
                )}
                <Combobox.Input
                  className="w-0 min-w-[48px] flex-1 border-none bg-transparent text-sm outline-none placeholder:text-gray-400 focus:outline-none focus:ring-0"
                  displayValue={() => core.query}
                  onChange={event => {
                    const newValue = event.target.value;
                    onInputChange?.(newValue);
                    core.setQuery(newValue);
                  }}
                  placeholder={selectedValues.length > 0 ? "" : placeholder}
                  readOnly={readOnly}
                  autoComplete="off"
                />
              </div>
              {!readOnly && (
                <ChevronDown className="ml-2 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden />
              )}
            </Combobox.Button>
          </div>
          {createPortal(
            <Combobox.Options
              ref={core.refs.setFloating}
              style={core.floatingStyles}
              className="pointer-events-auto z-[100] overflow-auto rounded-md bg-white text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
              data-scroll-lock-scrollable
            >
              <AutoCompleteContent
                filteredItems={core.filteredItems}
                query={core.query}
                addNewOption={addNewOption}
                isLoading={isLoading}
                getAddOptionText={getAddOptionText}
                renderOption={renderOption}
                multi
                selectedValues={selectedValues}
                options={options}
              />
            </Combobox.Options>,
            document.body,
          )}
        </Combobox>
      </div>
    );
  }

  return (
    <div
      className={cn(autoCompleteStyles.container({ size }), {
        hidden: !isDisplayed,
      })}
    >
      {label && (
        <div className="flex items-center gap-2">
          <label className={autoCompleteStyles.label({ size })}>{label}</label>
          {tooltip && (
            <BasicTooltip content={tooltip}>
              <Info className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700" />
            </BasicTooltip>
          )}
        </div>
      )}
      <Combobox
        disabled={isDisabled || readOnly}
        value={core.selectedItem}
        onChange={core.handleComboboxChange}
      >
        <div ref={core.refs.setReference} className="bg-white">
          <BasicTooltip
            content={selectedLabel}
            shouldShow={isSelectionOverflowing && !!selectedLabel}
          >
            <Combobox.Button
              className={cn(
                "relative flex w-full items-center justify-between",
                isDisabled && "pointer-events-none cursor-not-allowed opacity-50",
              )}
            >
              <Combobox.Input
                ref={inputRef}
                className={cn(autoCompleteStyles.input({ size }), !readOnly && "!pr-12")}
                displayValue={(item: AutoCompleteOption | null) =>
                  multi ? core.query : item?.label || value || ""
                }
                onChange={event => {
                  const newValue = event.target.value;
                  onInputChange?.(newValue);
                  core.setQuery(newValue);
                  if (addNewOption && !multi) {
                    onSelect?.(newValue);
                  }
                }}
                onBlur={core.handleInputBlur}
                placeholder={placeholder}
                readOnly={readOnly}
                autoComplete="off"
              />
              {!readOnly && (
                <div className="absolute inset-y-0 right-1.5 top-1/2 flex -translate-y-1/2 cursor-pointer items-center gap-1 pr-2">
                  {!addNewOption && (
                    <ChevronDown className="h-4 w-4 text-gray-400 hover:cursor-pointer hover:text-gray-500" />
                  )}
                  {value && !multi && (
                    <X
                      onClick={(e: React.MouseEvent<SVGSVGElement>) => core.handleClear(e)}
                      className="h-4 w-4 text-gray-400 hover:cursor-pointer hover:text-red-500"
                    />
                  )}
                </div>
              )}
            </Combobox.Button>
          </BasicTooltip>
        </div>
        {createPortal(
          <Combobox.Options
            ref={core.refs.setFloating}
            style={core.floatingStyles}
            className="pointer-events-auto z-[100] overflow-auto overscroll-none rounded-md bg-white text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            data-scroll-lock-scrollable
          >
            <AutoCompleteContent
              filteredItems={core.filteredItems}
              query={core.query}
              addNewOption={addNewOption}
              isLoading={isLoading}
              getAddOptionText={getAddOptionText}
              renderOption={renderOption}
            />
          </Combobox.Options>,
          document.body,
        )}
      </Combobox>
    </div>
  );
};
